/**
 * Runs `task` over every item with at most `limit` of them in flight.
 *
 * Resolves once every task has settled successfully, and rejects as soon as one
 * task rejects — tasks already started still run to completion, so a caller
 * that must not abandon the rest of the batch handles failure inside `task`.
 */
export async function runWithConcurrency<T>(
  items: readonly T[],
  limit: number,
  task: (item: T) => Promise<void>,
): Promise<void> {
  if (!Number.isInteger(limit) || limit < 1) {
    throw new RangeError(`concurrency limit must be a positive integer, got ${limit}`);
  }
  let next = 0;
  const worker = async (): Promise<void> => {
    while (next < items.length) {
      await task(items[next++]!);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, worker),
  );
}
